import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BuildingId, DeviceConfigId, Theme, ThemeStatus } from 'shared/models';
import { useAsync, useDeviceTypes, useSession } from 'hooks';
import { toError } from 'shared/util/toError';
import { Dialog } from 'ui/shared/containers';
import { ExportThemeData, ExportThemeForm, getThemeExtension } from '../exportThemeForm/ExportThemeForm';
import { callApi } from 'apiClient';
import { publishThemeSuccess, publishThemeFailure } from 'actions/themeActions';
import { s3Bucket } from 'util/env';
import { downloadWithProgress } from 'util/downloadWithProgress';
import { Permission } from 'shared/permissions';

export type Props = {
    theme: Theme | null;
    visible: boolean;
    elevatorId: string;
    buildingId: BuildingId;
    onClose(): void;
    onChangeElevatorId(value: string): void;
};

export const ExportThemeDialog = ({ theme, visible, elevatorId, buildingId, onClose, onChangeElevatorId }: Props) => {
    const { isMounted } = useAsync();
    const { hasPermission } = useSession();

    const abortControllerRef = useRef<AbortController>();
    const [ deviceConfigId, setDeviceConfigId ] = useState<DeviceConfigId | null>(null);
    const [ isExporting, setIsExporting ] = useState(false);
    const [ progress, setProgress ] = useState(0);
    const [ errorMessageKey, setErrorMessageKey ] = useState<string | null>(null);
    const [ blob, setBlob ] = useState<Blob>();
    const themeId = theme?.id;

    const { deviceTypes } = useDeviceTypes();
    const deviceTypeId = theme?.requirements?.deviceTypeId;
    const deviceType = deviceTypes[deviceTypeId!];
    const extension = getThemeExtension(deviceType);

    const dispatch = useDispatch();

    const handleExport = useCallback((data: ExportThemeData) => {
        if (!hasPermission(buildingId, Permission.EditDevice)) {
            setErrorMessageKey('ACCESS_DENIED');
            return;
        }
        
        setIsExporting(true);
        setErrorMessageKey(null);
        setProgress(0);

        abortControllerRef.current = new AbortController();

        void (async () => {
            try {
                const signal = abortControllerRef.current?.signal;

                const { elevatorId, deviceConfigId } = data;
                onChangeElevatorId(elevatorId);
                setDeviceConfigId(deviceConfigId);

                let theme = await callApi<Theme>('POST', `/api/themes/${themeId}/publish`, { buildingId, elevatorId, deviceConfigId });
                dispatch(publishThemeSuccess(theme));
        
                // Wait for theme to finish publishing
                let lastStatus = theme.status;
                while (!signal?.aborted && theme.status !== ThemeStatus.Error && theme.status !== ThemeStatus.Idle) {
                    await new Promise<void>(resolve => setTimeout(resolve, 950)); // Approximately once per second. Assume the get call will be ~50ms

                    // Check on the status of the theme again
                    theme = await callApi('GET', `/api/themes/${themeId}`, undefined, { signal, keepalive: true, cache: 'no-cache' });
        
                    // Update the store if the status changed since our last call
                    if (theme.status !== lastStatus) {
                        dispatch(publishThemeSuccess(theme));
                        lastStatus = theme.status;
                    }
                }

                if (signal?.aborted) return;

                // Bail out if the theme build failed
                if (lastStatus === ThemeStatus.Error) {
                    throw new Error('THEME_BUILD_FAILED');
                }

                // Download the exported theme
                const url = `https://${s3Bucket}.s3.amazonaws.com/content/${themeId}/theme.${extension}`;
                const blob = await downloadWithProgress(url, (_, progress) => {
                    if (!isMounted()) return;
                    setProgress(progress);
                }, signal);
                setBlob(blob);
            }
            catch (e) {
                const err = toError(e);
                dispatch(publishThemeFailure(err));
                isMounted() && setErrorMessageKey(err.message);
            }
            finally {
                if (isMounted()) {
                    setIsExporting(false);
                    setProgress(0);
                }
            }
        })();
    }, [ dispatch, hasPermission, buildingId, themeId, extension, onChangeElevatorId, setBlob, setDeviceConfigId, setIsExporting, setProgress, setErrorMessageKey, isMounted, abortControllerRef ]);

    // Reset the form when it becomes visible
    useEffect(() => {
        if (visible) {
            setBlob(undefined);
            setIsExporting(false);
            setErrorMessageKey(null);
            setProgress(0);
        }
    }, [ visible, setBlob, setIsExporting, setErrorMessageKey, setProgress ]);

    if (!deviceTypeId) {
        console.error('No device type ID specified');
        return null;
    }

    return (
        <Dialog
            id="export-theme-dialog"
            visible={ visible }
            titleKey="LABEL.EXPORT_THEME_FOR_OFFLINE"
        >
            <ExportThemeForm
                theme={ theme }
                buildingId={ buildingId }
                deviceTypeId={ deviceTypeId }
                elevatorId={ elevatorId }
                deviceConfigId={ deviceConfigId }
                isExporting={ isExporting }
                progress={ progress }
                blob={ blob }
                errorMessageKey={ errorMessageKey }
                onExport={ handleExport }
                onClose={ onClose }
            />
        </Dialog>
    );
};
